import * as React from 'react';
import { TextField, FormControl, InputLabel, MenuItem, Select, Dialog, DialogActions, DialogContent, DialogTitle, Button, InputAdornment } from "@mui/material";
import logo from '../../assets/img/logo_alt.png';
import { useEffect, useState } from 'react';
import { ApiRequest } from "GlobalFunctions";
import LoadingButton from '@mui/lab/LoadingButton';
import Box from "elements/Box";
import { useSnackbar } from 'notistack';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from './firebase';
import GoogleIcon from '@mui/icons-material/Google';

const validateRut = (rut: string): boolean => {
    const clean = rut.replace(/[.\-\s]/g, '').toUpperCase();
    if (!/^\d{7,8}[0-9K]$/.test(clean)) return false;
    const digits = clean.slice(0, -1);
    const dv = clean.slice(-1);
    let sum = 0, factor = 2;
    for (let i = digits.length - 1; i >= 0; i--) {
        sum += parseInt(digits[i]) * factor;
        factor = factor === 7 ? 2 : factor + 1;
    }
    const rem = 11 - (sum % 11);
    const expected = rem === 11 ? '0' : rem === 10 ? 'K' : String(rem);
    return dv === expected;
};

const formatRut = (value: string): string => {
    const clean = value.replace(/[^\dKk]/g, '').toUpperCase();
    if (clean.length <= 1) return clean;
    const dv = clean.slice(-1);
    const digits = clean.slice(0, -1);
    const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${formatted}-${dv}`;
};

export default function SignUp(props: any) {

    const [showEmailForm, setShowEmailForm] = useState(false);

    /*SNACKBAR FUNCTION FOR NOTIFICATIONS*/
    const { enqueueSnackbar } = useSnackbar();
    /*============================================= */

    const [fieldsCompleted, setFieldsCompleted] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [nurseAlertOpen, setNurseAlertOpen] = useState(false);
    const [googleRutOpen, setGoogleRutOpen] = useState(false);
    const [googleRut, setGoogleRut] = useState('');
    const [googlePhone, setGooglePhone] = useState('');
    const [googleRutSaving, setGoogleRutSaving] = useState(false);

    const defaultValues = {
        role_id: '' as number | '',
        name: '',
        lastname: '',
        phone: '',
        whatsapp: '',
        email: '',
        password: '',
        confirm_password: '',
        home_location: '',
        rut: '',
    }

    const [formValues, setFormValues] = useState(defaultValues);

    const validateEmail = (email: string) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    }

    const checkFields = () => {
        let valid = true;
        if (formValues.name === '') valid = false;
        if (formValues.lastname === '') valid = false;
        if (formValues.phone.length < 8) valid = false;
        if (formValues.email === '' || !validateEmail(formValues.email)) valid = false;
        if (formValues.password === '') valid = false;
        if (formValues.confirm_password === '') valid = false;
        if (formValues.password !== formValues.confirm_password) valid = false;
        if (formValues.role_id === 2 && formValues.home_location === '') valid = false;
        if (formValues.role_id === 2 && !validateRut(formValues.rut)) valid = false;

        setFieldsCompleted(valid);
    }

    useEffect(() => {
        if (localStorage.getItem('ltkn')) {
            window.location.href = '/';
        }
    }, []);

    const submit = async () => {
        setLoadingButton(true);
        await ApiRequest({
            url: '/users/signup',
            method: 'POST',
            body: formValues,
            setResponse: (response: any) => {
                setLoadingButton(false);
                if (response.status === 'error') {
                    enqueueSnackbar(response.message, { variant: 'error' });
                } else if (formValues.role_id === 2) {
                    setNurseAlertOpen(true);
                } else {
                    window.location.href = '/login';
                }
            }
        });
        
    }

    const submitGoogleRut = () => {
        setGoogleRutSaving(true);
        ApiRequest({
            url: '/users/updateRut',
            method: 'POST',
            body: { rut: googleRut, phone: googlePhone },
            setResponse: () => {
                setGoogleRutSaving(false);
                window.location.href = '/';
            }
        });
    };

    const signUpWithGoogle = async () => {
        try {
            setLoadingButton(true);
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;

            await ApiRequest({
                url: '/users/google-signup',
                method: 'POST',
                body: {
                    email: user.email,
                    name: user.displayName?.split(' ')[0] || '',
                    lastname: user.displayName?.split(' ').slice(1).join(' ') || '',
                    google_id: user.uid,
                    role_id: formValues.role_id
                },
                setResponse: (response: any) => {
                    setLoadingButton(false);
                    if (response.status === 'error') {
                        enqueueSnackbar(response.message, { variant: 'error' });
                    } else {
                        localStorage.setItem('ltkn', response.user_info.ltkn);
                        localStorage.setItem('user_name', response.user_info.user_name);
                        localStorage.setItem('role_id', response.user_info.role_id);
                        enqueueSnackbar(response.message, { variant: 'success' });
                        if (formValues.role_id === 2) {
                            setGoogleRutOpen(true);
                        } else {
                            window.location.href = '/';
                        }
                    }
                }
            });
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Error al registrarse con Google", { variant: 'error' });
            setLoadingButton(false);
        }
    };

    useEffect(() => {
        checkFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues]);

    return (
        <>
            <Box id='loginBox' className='intent-auth_component'>

                <img className='loginLogo' src={logo} alt="" />

                <div className="input_group">

                    <FormControl fullWidth>
                      <InputLabel id="role-label">Seleccione tipo de usuario</InputLabel>
                      <Select
                          labelId="role-label"
                          id="role"
                          value={formValues.role_id}
                          label="Seleccione tipo de usuario"
                          onChange={(e) => setFormValues({ ...formValues, role_id: e.target.value as number })}
                      >
                          <MenuItem value={1}>Paciente</MenuItem>
                          <MenuItem value={2}>Enfermera</MenuItem>
                      </Select>
                    </FormControl>

                    <LoadingButton
                        size="small"
                        fullWidth
                        variant="outlined"
                        sx={{ fontSize: '13px', padding: '10px 0px', marginTop: '10px' }}
                        onClick={signUpWithGoogle}
                        disabled={loadingButton || formValues.role_id === ''}
                        loading={loadingButton}
                        startIcon={<GoogleIcon />}
                    >
                        Registrarme con Google
                    </LoadingButton>

                    <LoadingButton
                        size="small"
                        fullWidth
                        variant="outlined"
                        sx={{ fontSize: '13px', padding: '10px 0px', marginTop: '10px' }}
                        onClick={() => setShowEmailForm(!showEmailForm)}
                        disabled={formValues.role_id === ''}
                    >
                        {showEmailForm ? 'Ocultar formulario de correo' : 'Registrarme con correo'}
                    </LoadingButton>

                    {showEmailForm && (
                        <>
                            <div className='input_row'>
                                <TextField
                                    label="Nombre"
                                    value={formValues.name}
                                    onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                                    variant="outlined"
                                    fullWidth
                                />

                                <TextField
                                    label="Apellido"
                                    value={formValues.lastname}
                                    onChange={(e) => setFormValues({ ...formValues, lastname: e.target.value })}
                                    variant="outlined"
                                    fullWidth
                                />
                            </div>

                            <TextField
                                label="Número de Teléfono"
                                value={formValues.phone}
                                onChange={(e) => {
                                    const digits = e.target.value.replace(/\D/g, '');
                                    setFormValues({ ...formValues, phone: digits, whatsapp: '+56' + digits });
                                }}
                                variant="outlined"
                                fullWidth
                                inputProps={{ maxLength: 9 }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">+56</InputAdornment>
                                }}
                                helperText="Ingresa solo los dígitos sin el +56. Ej: 912345678"
                            />

                            {formValues.role_id === 2 && (
                                <TextField
                                    label="Comuna de residencia"
                                    value={formValues.home_location}
                                    onChange={(e) => setFormValues({ ...formValues, home_location: e.target.value })}
                                    variant="outlined"
                                    fullWidth
                                    helperText="Indica dónde vives para mejorar las derivaciones cercanas"
                                />
                            )}

                            {formValues.role_id === 2 && (
                                <TextField
                                    label="RUT *"
                                    value={formValues.rut}
                                    onChange={(e) => setFormValues({ ...formValues, rut: formatRut(e.target.value) })}
                                    variant="outlined"
                                    fullWidth
                                    error={formValues.rut !== '' && !validateRut(formValues.rut)}
                                    helperText={
                                        formValues.rut !== '' && !validateRut(formValues.rut)
                                            ? 'RUT inválido — formato: 12.345.678-9'
                                            : 'Requerido para emitir boletas. Ej: 12.345.678-9'
                                    }
                                    inputProps={{ maxLength: 12 }}
                                />
                            )}

                            <TextField
                                label="Correo Electrónico"
                                value={formValues.email}
                                onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
                                error={formValues.email !== '' && !validateEmail(formValues.email)}
                                helperText={formValues.email !== '' && !validateEmail(formValues.email) ? 'Correo inválido' : ''}
                                variant="outlined"
                                fullWidth
                            />

                            <TextField
                                label="Contraseña"
                                type="password"
                                value={formValues.password}
                                onChange={(e) => setFormValues({ ...formValues, password: e.target.value })}
                                variant="outlined"
                                fullWidth
                            />

                            <TextField
                                label="Confirmar Contraseña"
                                type="password"
                                value={formValues.confirm_password}
                                onChange={(e) => setFormValues({ ...formValues, confirm_password: e.target.value })}
                                error={formValues.password !== formValues.confirm_password && formValues.confirm_password !== ''}
                                helperText={formValues.password !== formValues.confirm_password && formValues.confirm_password !== '' ? 'Las contraseñas no coinciden' : ''}
                                variant="outlined"
                                fullWidth
                            />


                            <LoadingButton
                                size="small"
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ fontSize: '13px', padding: '10px 0px' }}
                                onClick={submit}
                                disabled={!fieldsCompleted || loadingButton}
                                loading={loadingButton}
                            >
                                Registrarme
                            </LoadingButton>
                        </>
                    )}
                    
                </div>
            </Box>

            {/* Dialog RUT post-registro con Google (solo enfermeras) */}
            <Dialog open={googleRutOpen} maxWidth="xs" fullWidth disableEscapeKeyDown>
                <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 0 }}>
                    Ingresa tu RUT
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <p style={{ margin: '0 0 16px 0', color: '#374151', fontSize: 14, lineHeight: 1.6 }}>
                        Para completar tu registro como enfermera necesitamos tu RUT y teléfono de contacto.
                    </p>
                    <TextField
                        label="RUT *"
                        value={googleRut}
                        onChange={(e) => setGoogleRut(formatRut(e.target.value))}
                        fullWidth
                        variant="outlined"
                        inputProps={{ maxLength: 12 }}
                        error={googleRut !== '' && !validateRut(googleRut)}
                        helperText={googleRut !== '' && !validateRut(googleRut) ? 'RUT inválido' : 'Ej: 12.345.678-9'}
                        autoFocus
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Teléfono *"
                        value={googlePhone}
                        onChange={(e) => setGooglePhone(e.target.value.replace(/\D/g, ''))}
                        fullWidth
                        variant="outlined"
                        inputProps={{ maxLength: 9 }}
                        helperText="Ej: 912345678"
                        InputProps={{ startAdornment: <InputAdornment position="start">+56</InputAdornment> }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: '12px 24px 20px' }}>
                    <Button
                        variant="contained"
                        fullWidth
                        disabled={!validateRut(googleRut) || googlePhone.length < 8 || googleRutSaving}
                        onClick={submitGoogleRut}
                    >
                        {googleRutSaving ? 'Guardando...' : 'Guardar y continuar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Alerta de certificados para enfermeras recién registradas */}
            <Dialog open={nurseAlertOpen} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, fontSize: 19, pb: 0 }}>
                    🎉 ¡Registro exitoso!
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                        <span style={{ fontSize: 30, flexShrink: 0 }}>📋</span>
                        <p style={{ margin: 0, color: '#374151', lineHeight: 1.7, fontSize: 15 }}>
                            Recuerda subir tus <strong>certificados y credenciales profesionales</strong> en tu perfil para quedar aprobada como Especialista en Heridas. De lo contrario, nos contactaremos contigo para solicitarlos.
                        </p>
                    </div>
                </DialogContent>
                <DialogActions sx={{ p: '12px 24px 20px' }}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => { setNurseAlertOpen(false); window.location.href = '/login'; }}
                    >
                        Entendido, iniciar sesión
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
